import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import type { CaseReason, CaseSource } from 'types/interfaces/thread';
import type {
  CaseListResponse,
  Case,
  CaseDetailResponse,
  TradeCaseStatusResponse,
  SummonAdminResponse,
  CaseBadgeCountResponse,
  AdminActionResponse,
  SendAdminMessageResponse,
  SetDueDateResponse
} from 'types/interfaces/case';

export interface SummonAdminRequest {
  tradeId: string;
  reason: CaseReason;
  affectedItemIds?: string[];
  images?: File[];
  description?: string;
  source: CaseSource;
}

export const caseApi = createApi({
  reducerPath: 'caseApi',
  baseQuery: baseQuery,
  tagTypes: ['Cases', 'CaseDetail', 'CaseBadgeCount'],
  endpoints: (builder) => ({
    // User-facing endpoints
    /**
     * Summon Admin wizard (Asana 1219952262403623). Multipart: the reason is
     * required, affected cards are required for RECEIVER_NOT_ARRIVED and
     * RECEIVER_ARRIVED_INCORRECT, one to three photos for ARRIVED_INCORRECT,
     * the description is optional (max 2000). `affectedItemIds` is appended as
     * a repeated field; the backend accepts one value or many.
     */
    summonAdmin: builder.mutation<SummonAdminResponse, SummonAdminRequest>({
      query: ({ tradeId, reason, affectedItemIds, images, description, source }) => {
        const formData = new FormData();
        formData.append('reason', reason);
        formData.append('source', source);
        (affectedItemIds ?? []).forEach((id) => formData.append('affectedItemIds', id));
        if (description && description.trim()) formData.append('description', description.trim());
        (images ?? []).forEach((file) => formData.append('images', file));
        return {
          url: `/trades/${tradeId}/summon-admin`,
          method: 'POST',
          body: formData,
          requiresAuth: true,
        };
      },
      invalidatesTags: ['Cases', 'CaseBadgeCount'],
    }),

    getTradeCase: builder.query<TradeCaseStatusResponse, string>({
      query: (tradeId) => ({
        url: `/trades/${tradeId}/case`,
        method: 'GET',
        requiresAuth: true,
      }),
    }),

    // Admin endpoints
    getCases: builder.query<CaseListResponse, {
      page?: number;
      pageSize?: number;
      /** unread and due_today are server-side quick filters (review ruling B). */
      status?: 'unresolved' | 'unread' | 'due_today' | 'resolved' | 'all';
      search?: string;
      sortBy?: 'caseDate' | 'tradeDate';
      sortOrder?: 'asc' | 'desc';
    }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        if (params.status) queryParams.append('status', params.status);
        if (params.search) queryParams.append('search', params.search);
        if (params.sortBy) queryParams.append('sortBy', params.sortBy);
        if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

        return {
          url: `/admin/cases?${queryParams.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['Cases'],
    }),

    getCaseById: builder.query<CaseDetailResponse, string>({
      query: (caseId) => ({
        url: `/admin/cases/${caseId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (result, error, caseId) => [
        { type: 'CaseDetail', id: caseId },
        'Cases'
      ],
    }),

    getCaseBadgeCount: builder.query<CaseBadgeCountResponse, void>({
      query: () => ({
        url: '/admin/cases/badge/count',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['CaseBadgeCount'],
    }),

    sendAdminMessage: builder.mutation<SendAdminMessageResponse, {
      caseId: string;
      content: string
    }>({
      query: ({ caseId, content }) => ({
        url: `/admin/cases/${caseId}/messages`,
        method: 'POST',
        body: { content },
        requiresAuth: true,
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'CaseDetail', id: caseId },
        'Cases'
      ],
    }),

    performAdminAction: builder.mutation<AdminActionResponse, {
      caseId: string;
      actionType: string;
      details?: any;
    }>({
      query: ({ caseId, actionType, details }) => ({
        url: `/admin/cases/${caseId}/actions`,
        method: 'POST',
        body: { actionType, details },
        requiresAuth: true,
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'CaseDetail', id: caseId },
        'Cases',
        'CaseBadgeCount'
      ],
    }),

    /** Reopen a RESOLVED or CLOSED case (Asana 1219265535165249): back to IN_PROGRESS, due date cleared. */
    reopenCase: builder.mutation<{ message?: string; case?: Case }, { caseId: string; reason?: string }>({
      query: ({ caseId, reason }) => ({
        url: `/admin/cases/${caseId}/reopen`,
        method: 'POST',
        body: reason ? { reason } : {},
        requiresAuth: true,
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'CaseDetail', id: caseId },
        'Cases',
        'CaseBadgeCount',
      ],
    }),
    setDueDate: builder.mutation<SetDueDateResponse, {
      caseId: string;
      dueDate: string
    }>({
      query: ({ caseId, dueDate }) => ({
        url: `/admin/cases/${caseId}/due-date`,
        method: 'PUT',
        body: { dueDate },
        requiresAuth: true,
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'CaseDetail', id: caseId },
        'Cases'
      ],
    }),
  }),
});

export const {
  useSummonAdminMutation,
  useGetTradeCaseQuery,
  useGetCasesQuery,
  useGetCaseByIdQuery,
  useGetCaseBadgeCountQuery,
  useSendAdminMessageMutation,
  usePerformAdminActionMutation,
  useSetDueDateMutation,
  useReopenCaseMutation,
} = caseApi;
